import { useCallback, useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SelectionBar } from "@/components/dash/SelectionBar";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/dash/AppShell";
import { LeadCard } from "@/components/dash/LeadCard";
import { ChipRow } from "@/components/dash/FilterChips";
import { TrialBanner } from "@/components/dash/Paywall";
import { StatCards } from "@/components/dash/StatCards";
import { STATUSES, QUALITIES } from "@/lib/constants";

const EmptyIllustration = () => (
  <svg viewBox="0 0 160 110" className="mx-auto h-28 w-40" aria-hidden="true">
    <rect x="30" y="20" width="100" height="74" rx="10" className="fill-muted stroke-border" strokeWidth="2" />
    <path d="M30 60h28l6 10h32l6-10h28" fill="none" className="stroke-border" strokeWidth="2" />
    <path d="M62 40l18-14 18 14v18H62z" fill="none" stroke="var(--brand)" strokeWidth="2.5" strokeLinejoin="round" />
    <rect x="75" y="46" width="10" height="12" rx="1.5" fill="var(--brand)" opacity="0.35" />
    <circle cx="128" cy="22" r="10" fill="var(--brand)" opacity="0.15" />
    <path d="M124 22h8M128 18v8" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const EmptyState = ({ slug, filtered }) => {
  const link = `${window.location.origin}/q/${slug}`;
  return (
    <div className="rounded-2xl border border-dashed bg-card px-6 py-12 text-center" data-testid="dashboard-empty-state">
      <EmptyIllustration />
      <p className="mt-4 text-base font-semibold text-foreground">{filtered ? "No leads match these filters" : "No project requests yet"}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
        {filtered ? "Try another status or quality filter." : "Share your quote link on your website, Google profile and texts. New requests show up here instantly."}
      </p>
      {!filtered && (
        <button type="button" data-testid="empty-copy-link-button"
          onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copied"))}
          className="mx-auto mt-6 flex h-11 items-center gap-2 rounded-xl bg-[color:var(--brand)] px-5 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
          <Copy className="h-4 w-4" /> Copy my quote link
        </button>
      )}
    </div>
  );
};

const Skeleton = () => (
  <div className="flex gap-4 rounded-2xl border bg-card p-3">
    <div className="h-24 w-24 animate-pulse rounded-xl bg-muted" />
    <div className="flex-1 space-y-3 py-1"><div className="h-4 w-1/2 animate-pulse rounded bg-muted" /><div className="h-3 w-1/3 animate-pulse rounded bg-muted" /><div className="h-6 w-2/3 animate-pulse rounded bg-muted" /></div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState(null);
  const [status, setStatus] = useState(null);
  const [quality, setQuality] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (quality) params.quality = quality;
    return api.get("/leads", { params }).then(({ data }) => setLeads(data));
  }, [status, quality]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected((s) => new Set([...s].filter((id) => leads?.some((l) => l.id === id)))); }, [leads]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const confirmDelete = async () => {
    const ids = toDelete;
    setToDelete(null);
    try {
      const { data } = await api.post("/leads/bulk-delete", { ids });
      toast.success(`${data.deleted} ${data.deleted === 1 ? "lead" : "leads"} deleted`);
      setSelected(new Set());
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const pending = leads?.some((l) => l.ai_status === "pending");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [pending, load]);

  return (
    <AppShell>
      <TrialBanner />
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Leads</h1>
          <p className="mt-1 text-sm text-muted-foreground">Hot leads first, then the newest.</p>
        </div>
        {leads && <span className="text-sm text-muted-foreground" data-testid="dashboard-lead-count">{leads.length} {leads.length === 1 ? "request" : "requests"}</span>}
      </div>
      <div className="mt-5"><StatCards refreshKey={leads?.length} /></div>
      <div className="mt-6 space-y-2">
        <ChipRow label="Quality" prefix="quality" options={QUALITIES.map((q) => ({ key: q, label: q }))} value={quality} onChange={setQuality} />
        <ChipRow label="Status" prefix="status" options={STATUSES} value={status} onChange={setStatus} />
      </div>
      {leads && <SelectionBar total={leads.length} selected={selected.size} onSelectAll={() => setSelected(new Set(leads.map((l) => l.id)))}
        onClear={() => setSelected(new Set())} onDelete={() => setToDelete([...selected])} />}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="dashboard-lead-list">
        {leads === null ? [0, 1, 2, 3].map((i) => <Skeleton key={i} />)
          : leads.length === 0 ? <div className="md:col-span-2"><EmptyState slug={user.slug} filtered={!!(status || quality)} /></div>
          : leads.map((l) => <LeadCard key={l.id} lead={l} selected={selected.has(l.id)} onToggle={toggle} onDelete={(id) => setToDelete([id])} />)}
      </div>
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} testId="lead-delete-dialog" confirm="Delete"
        title={toDelete?.length > 1 ? `Delete ${toDelete.length} leads?` : "Delete this lead?"}
        description="Are you sure? This can't be undone. Photos and preview images are deleted too." onConfirm={confirmDelete} />
    </AppShell>
  );
}
